import { useCallback, useEffect, useState } from 'react';
import type { AgentTurn, ProjectRole, ProposalSummary } from '@integrate/contracts';
import type { SeparationState } from '@integrate/core';
import { api } from '../api.js';
import { ICON, IconButton } from '../ui/Icon.js';
import { AssistantPanel } from './AssistantPanel.js';
import { bulkIntent } from './SelectionPanel.js';
import { browserCapture, pickContainer, type CaptureEnvironment } from '../voice/capture.js';
import type { RealtimeAudioEnvironment } from '../voice/realtime-audio.js';
import type { OpenVoiceSocket } from '../voice/realtime-session.js';

/**
 * Where the assistant lives now: a launcher in the corner, and a panel that floats above the workspace.
 *
 * IT WAS A BUTTON IN A CROWDED HEADER AND A MODAL OVER THE CHART. Both are gone. The modal was the
 * wrong shape for what this is — a modal is an interruption you complete and dismiss, and asking a
 * question about the design in front of you is the opposite: you want to keep looking at the thing you
 * are asking about. The dock overlaps the reading column and leaves the chart alone.
 *
 * WHY BOTTOM-LEFT, HAVING BEEN BOTTOM-RIGHT. The original argument was that the left is over the
 * chart, where `OrgChart.tsx` has an `onClickCapture` and d3 owns pan and zoom, so a floating surface
 * there competes for the same gestures — and that a reading column is cheaper to cover than a canvas.
 *
 * It weighed a possible collision against a certain one. The right edge is where the side panel is,
 * so the dock landed on top of it every time somebody opened the assistant while reading a person, a
 * group or a phase — which is exactly when they would ask about it. The chart is a canvas that pans:
 * whatever a panel covers there can be dragged out from under it. See `styles.css` for the rest.
 *
 * THE STATE THAT LIVES HERE, and why it is not in the panel: the TURNS. They outlive the panel, which
 * is unmounted on close so the microphone is released (`AssistantPanel`'s unmount effect, asserted by
 * its test). Keeping the thread here means closing the dock and reopening it does not re-fetch, while
 * the mic still goes cold the moment the feature is visibly over.
 */
export type AssistantMode = 'dictate' | 'talk';

/**
 * Which microphone this reader last chose, across projects and across reloads.
 *
 * REMEMBERED FOR THE SAME REASON THE PANEL SECTIONS ARE. A separation workshop is one person working
 * one way for an hour; a preference that reset on every reload would be re-answered dozens of times,
 * which is exactly the friction the single button exists to remove.
 *
 * `talk` IS THE DEFAULT ONLY WHERE IT IS POSSIBLE — the toggle is not offered unless both are — and
 * `dictate` is what a stored value falling outside the pair reads as, because it is the older and
 * more conservative of the two: it fills a box for somebody to read rather than opening a microphone.
 */
const MODE_KEY = 'integrate:assistant-mode';

function rememberedMode(): AssistantMode {
  try {
    return window.localStorage.getItem(MODE_KEY) === 'talk' ? 'talk' : 'dictate';
  } catch {
    // A browser with storage disabled still gets a working assistant, just not a remembered one.
    return 'dictate';
  }
}

function rememberMode(mode: AssistantMode): AssistantMode {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* See above. */
  }
  return mode;
}

export function AssistantDock({
  projectId,
  scenarioId,
  state,
  role,
  assistantAvailable,
  voiceAvailable,
  realtimeAvailable,
  myUserId,
  captureEnvironment,
  realtimeAudio,
  openVoiceSocket,
  onOpenProposals,
}: {
  projectId: string;
  /** The scenario the reader is standing in. Both the request's scope and the transcript's labels. */
  scenarioId: string | null;
  /** The design, for naming people in the rendered changes. Null while it loads. */
  state: SeparationState | null;
  role: ProjectRole | null;
  /** The deployment's own answer, learned at sign-in. */
  assistantAvailable: boolean;
  voiceAvailable: boolean;
  /** Whether a spoken conversation is configured. A third deployment, reported on its own. */
  realtimeAvailable: boolean;
  /** Who is reading, so a proposal they raised offers Withdraw rather than a button that is refused. */
  myUserId: string | null;
  captureEnvironment?: CaptureEnvironment;
  realtimeAudio?: RealtimeAudioEnvironment;
  openVoiceSocket?: OpenVoiceSocket;
  onOpenProposals(): void;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [chosenMode, setChosenMode] = useState<AssistantMode>(rememberedMode);
  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  /*
    THE THREAD IS FETCHED ONCE, when the dock is first opened.

    Not at mount: a person who never opens this should not pay a request for it on every project they
    enter. Not on every open either — `loaded` is the latch — because the turns here are the ones this
    browser has been appending, and re-fetching would only matter for a second machine, which is the
    reload's job rather than the open's.
  */
  useEffect(() => {
    if (!open || loaded) return;
    let live = true;
    void api
      .assistantThread(projectId)
      .then((result) => {
        if (!live) return;
        setTurns(result.turns);
        setLoaded(true);
      })
      .catch((cause: unknown) => {
        /*
          A thread that could not be read is reported and the composer stays usable. The alternative —
          refusing to open — would make a transient failure on a route that only READS take away the
          route that WRITES, which is the more valuable half.
        */
        if (!live) return;
        setError(cause instanceof Error ? cause.message : 'The conversation could not be loaded.');
        setLoaded(true);
      });
    return () => {
      live = false;
    };
  }, [open, loaded, projectId]);

  /*
    ESCAPE CLOSES, which is the opposite of what `Workbench` decided and for a stated reason.

    A workbench refuses Escape because closing one discards an arrangement somebody built and cannot get
    back. Closing this discards nothing at all — the thread is on the server and the box is the only
    thing lost, which is one keystroke of state. Where cancelling is free, Escape should do it.
  */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /*
    A DECIDED PROPOSAL REPLACES ITS OWN SUMMARY IN THE TURN, rather than the thread refetching.

    The turn is otherwise unchanged — same prompt, same restatement, same rendered changes — and the
    one thing that moved is the status. Refetching the thread to learn it would throw away and rebuild
    everything the reader is looking at, in a panel they are mid-conversation in.
  */
  const settle = useCallback((turnId: string, proposal: ProposalSummary): void => {
    setTurns((prev) => prev.map((turn) => (turn.id === turnId ? { ...turn, proposal } : turn)));
  }, []);

  const clear = useCallback((): void => {
    setError('');
    void api
      .clearAssistantThread(projectId)
      .then(() => setTurns([]))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'The conversation could not be cleared.'),
      );
  }, [projectId]);

  /*
    THE GATE, MOVED HERE VERBATIM from the header button it replaces.

    `assistantAvailable` is the deployment's own answer: a control whose only possible outcome is "not
    configured" is worse than no control. `bulkIntent` is the role's: a viewer cannot act on a
    suggestion, and a reviewer can — the answer becomes a proposal an editor reads — so the line is
    drawn there rather than at write access.
  */
  /*
    THE BROWSER'S HALF OF THE DICTATION QUESTION, asked here because the TOGGLE needs it — the panel
    asks it again for the button itself. `pickContainer` returns null rather than a default precisely
    so this has an answer: a default would mean recording in a format nobody accepts and finding out
    after the upload.
  */
  const canDictate = voiceAvailable && pickContainer((captureEnvironment ?? browserCapture()).isTypeSupported) !== null;

  /*
    A REMEMBERED PREFERENCE CANNOT SELECT A MODE THIS DEPLOYMENT DOES NOT HAVE.

    The stored value is a choice between two things, and most deployments have one or neither. Read
    literally it would hide the only microphone there is: a tenant with realtime and no transcription
    kept `dictate` from some other project and got a composer with no start button at all — which is
    what the two capability tests caught the moment the toggle went in.

    So the preference applies where there is a choice, and where there is not, the available one wins.
  */
  const mode: AssistantMode = !canDictate ? 'talk' : !realtimeAvailable ? 'dictate' : chosenMode;

  if (!assistantAvailable || bulkIntent(role) === 'none') return null;

  return (
    <>
      <IconButton
        className="assistant-launcher"
        testId="open-assistant"
        icon={ICON.assistant}
        label={
          open
            ? 'Close the assistant'
            : 'Ask the assistant. Whatever it suggests becomes a proposal somebody reviews.'
        }
        onClick={() => setOpen((v) => !v)}
      />

      {open && (
        <div className="assistant-dock" data-testid="assistant-dock" role="dialog" aria-label="Assistant">
          <div className="assistant-head">
            <strong>Assistant</strong>
            <span className="spacer" />
            {/*
              THE TOGGLE IS A SETTING, WHICH IS WHY IT IS UP HERE RATHER THAN BESIDE THE BUTTON.

              Dictation and a spoken conversation are two ways of doing the same thing, not two things
              to do. Offered side by side in the composer they asked the reader to re-decide on every
              single request; offered once, at the top, the decision is made for the session and the
              bottom of the panel has one button that starts the microphone.

              ONLY WHEN THERE IS A CHOICE. A deployment with one of the two capabilities has nothing to
              switch between, and a toggle with one reachable position is furniture.
            */}
            {canDictate && realtimeAvailable && (
              <div className="assistant-mode" role="group" aria-label="Microphone mode">
                {(['dictate', 'talk'] as const).map((option) => (
                  <button
                    key={option}
                    data-testid={`assistant-mode-${option}`}
                    className={mode === option ? 'active' : ''}
                    aria-pressed={mode === option}
                    onClick={() => setChosenMode(rememberMode(option))}
                    title={
                      option === 'dictate'
                        ? 'Dictate a request into the box, and read it before sending.'
                        : 'Talk to the assistant and have it answer out loud.'
                    }
                  >
                    {option === 'dictate' ? 'Dictate' : 'Talk'}
                  </button>
                ))}
              </div>
            )}
            {turns.length > 0 && (
              /*
                THE WHOLE RETENTION CONTROL, and it is offered only when there is something to remove.
                There is no expiry job and no window: a thread lives until the person deletes it or the
                project is. Hidden on an empty thread because a Clear that does nothing invites somebody
                to press it to find out what it does.
              */
              <IconButton
                testId="assistant-clear"
                icon={ICON.destroy}
                label="Delete this conversation. It cannot be recovered."
                onClick={clear}
              />
            )}
            <IconButton
              testId="assistant-close"
              icon={ICON.remove}
              label="Close the assistant"
              onClick={() => setOpen(false)}
            />
          </div>

          {error && (
            <p className="error" data-testid="assistant-dock-error" role="alert">
              {error}
            </p>
          )}

          <AssistantPanel
            projectId={projectId}
            scenarioId={scenarioId}
            turns={turns}
            state={state}
            voiceAvailable={voiceAvailable}
            realtimeAvailable={realtimeAvailable}
            mode={mode}
            role={role}
            myUserId={myUserId}
            onProposalSettled={settle}
            captureEnvironment={captureEnvironment}
            realtimeAudio={realtimeAudio}
            openVoiceSocket={openVoiceSocket}
            onAsked={(turn) => setTurns((prev) => [...prev, turn])}
            onOpenProposals={onOpenProposals}
          />
        </div>
      )}
    </>
  );
}
